export { onChangeEmail }

import { getCustomerOrAbort } from '../../server/auth/getCustomerOrAbort.ts'
import { rateLimitCustomerWrite } from '../../server/auth/rateLimitCustomerWrite.ts'
import { sendEmailChangeNotice } from '../../server/auth/sendEmailChangeNotice.ts'
import { updateCustomerEmail } from '../../server/db/queries.ts'
import { RATE_LIMITED_MESSAGE } from '../../utils/messages.ts'

// The auth cookie identifies the customer by UUID; changing the email keeps the session.
async function onChangeEmail(
  newEmail: string,
): Promise<{ inputErrors: { email: string } } | { email: string; noOp: boolean }> {
  const { customerId, env } = getCustomerOrAbort()
  if (!(await rateLimitCustomerWrite(customerId, env))) {
    return { inputErrors: { email: RATE_LIMITED_MESSAGE } }
  }
  if (!isValidEmail(newEmail)) {
    return { inputErrors: { email: 'Please enter a valid email address.' } }
  }
  const result = await updateCustomerEmail(customerId, newEmail, env)
  if (result.status === 'taken') {
    return { inputErrors: { email: 'That email is already in use by another customer.' } }
  }
  if (result.status === 'ok') {
    // Notify the previous address so a compromised session can't silently transfer
    // ownership. Best-effort: the change has already been committed; a Resend failure
    // shouldn't unwind it (sendEmail logs the failure internally).
    await sendEmailChangeNotice({ oldEmail: result.oldEmail, newEmail: result.newEmail, customerId }, env).catch(
      () => {},
    )
  }
  return { email: result.newEmail, noOp: result.status === 'noOp' }
}

// Conservative: at least one @, one . after the @, no whitespace.
function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)
}
